import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HeartPulse, LogOut, MapPin, User } from 'lucide-react';
import type { PublicAccount } from '@his/shared';
import { publicGet, publicSession } from '@/lib/public-api';
import { Button } from '@/components/ui/primitives';
import { PublicSignInDialog } from './PublicSignInDialog';
import { ThemeToggle } from '@/components/ThemeToggle';

interface PublicAuth {
  account: PublicAccount | null;
  loading: boolean;
  /** Opens the sign-in sheet and resolves once the visitor is signed in. */
  requireSignIn: () => void;
  signOut: () => void;
}

const PublicAuthContext = createContext<PublicAuth>({
  account: null,
  loading: true,
  requireSignIn: () => {},
  signOut: () => {},
});

export const usePublicAuth = () => useContext(PublicAuthContext);

/**
 * The patient-facing shell.
 *
 * Nothing in here assumes a hospital: a visitor arrives with a city and a
 * problem, not with a tenant. Signing in is only ever asked for at the moment
 * something has to be sent — browsing is anonymous by design.
 */
export function PublicLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [signingIn, setSigningIn] = useState(false);

  const account = useQuery({
    queryKey: ['public-me'],
    queryFn: () => publicGet<PublicAccount>('/me'),
    enabled: Boolean(publicSession.token()),
    retry: false,
  });

  useEffect(() => {
    const onSignedOut = () => {
      void queryClient.setQueryData(['public-me'], null);
    };
    window.addEventListener('his:public-signed-out', onSignedOut);
    return () => window.removeEventListener('his:public-signed-out', onSignedOut);
  }, [queryClient]);

  const value: PublicAuth = {
    account: account.data ?? null,
    loading: account.isLoading,
    requireSignIn: () => setSigningIn(true),
    signOut: () => {
      publicSession.signOut();
      queryClient.setQueryData(['public-me'], null);
      navigate('/find');
    },
  };

  return (
    <PublicAuthContext.Provider value={value}>
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
            <Link to="/find" className="flex items-center gap-2 font-semibold focus-ring rounded-md">
              <HeartPulse className="h-5 w-5 text-primary" />
              <span>Find care</span>
            </Link>

            <div className="flex items-center gap-2">
              <ThemeToggle />
              {value.account ? (
                <>
                  <Link
                    to="/find/me"
                    className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium hover:bg-accent focus-ring"
                  >
                    <User className="h-4 w-4" />
                    {value.account.name ?? value.account.phone}
                  </Link>
                  <Button variant="ghost" size="icon" aria-label="Sign out" onClick={value.signOut}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={() => setSigningIn(true)}>
                  Sign in
                </Button>
              )}
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-5xl px-4 py-6">
          <Outlet />
        </main>

        <footer className="mx-auto max-w-5xl px-4 pb-10 pt-4 text-xs text-muted-foreground">
          Appointments and orders placed here are requests. The hospital, lab or pharmacy confirms
          each one before it becomes final.
        </footer>
      </div>

      {signingIn && (
        <PublicSignInDialog
          open={signingIn}
          onClose={() => setSigningIn(false)}
          onSignedIn={() => {
            setSigningIn(false);
            void queryClient.invalidateQueries({ queryKey: ['public-me'] });
          }}
        />
      )}
    </PublicAuthContext.Provider>
  );
}

export function CityBadge({ city, onChange }: { city: string; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm hover:bg-accent focus-ring"
    >
      <MapPin className="h-3.5 w-3.5 text-primary" />
      {city || 'Choose a city'}
      <span className="text-xs text-muted-foreground">Change</span>
    </button>
  );
}

export function PublicSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}
